import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { ChevronRight, Check, Plus, Pencil, Trash2, CircleCheck, Building, Compass, Receipt } from "lucide-react";
import { Button, Divider, Input, Select, message } from "antd";
import { nanoid } from "nanoid";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import TestingGuide from "components/testing-guide/testing-guide";
import AddPropertyModal from "components/add-property-modal/add-property-modal";
import { PropertyFormOutput, uploadPropertyFormData } from "components/property-form/property-form";
import JurisdictionAutoNote from "components/jurisdiction-auto-note/jurisdiction-auto-note";

import { useGetPropertiesByOrganizationIdQuery } from "graphql/queries/get-properties-by-organization-id.generated";
import { useGetJurisdictionsQuery } from "graphql/queries/get-jurisdictions.generated";
import { useGetPlanReviewSubmissionTypesQuery } from "graphql/queries/get-plan-review-submission-types.generated";
import { useGetAhjFeeScheduleQuery } from "graphql/queries/get-ahj-fee-schedule.generated";
import {
	useCalculatePlanReviewFeeQuery,
	CalculatePlanReviewFeeQuery
} from "graphql/queries/calculate-plan-review-fee.generated";
import { CreatePropertyInput } from "graphql/generated/graphql";

import { getPropertyAddress } from "utils/helpers";
import { useResolvedJurisdiction } from "hooks/useResolvedJurisdiction";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { usePlanReviewFlowStore } from "state/zustand-stores/plan-review-flow-store";
import routeNames from "constants/route-names";
import { isAuthorityRole, isPlatformAdminRole } from "constants/field-values";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";

const TEMP_PROPERTY_PREFIX = "__temp_property__";

const REQUIREMENTS = ["All PDFs must be flattened", "No password protection"];

// Explains to the applicant that Ollie only pre-screens the plans and that the AHJ still
// reviews the application and issues the permit.
const APPLICANT_GUIDANCE_ITEMS = [
	{
		icon: Building,
		title: "Ollie is not the AHJ.",
		text: "Ollie gets your plans review-ready, while the AHJ grants official approvals and issues permits."
	},
	{
		icon: Compass,
		title: "Submitting to the AHJ comes next.",
		text: "Once Ollie approves your plans, you'll be ready to submit your application to the AHJ for official review."
	},
	{
		icon: Receipt,
		title: "Ollie is a pre-screening service.",
		text: "AHJ review and permit fees are separate and still apply."
	}
];

/**
 * "Plan Review" — the intro/setup screen.
 *
 * The organization is always the logged user's own. The property can be created here
 * without leaving the flow, the same way the inspection setup does it: it is staged
 * locally (with a temp id) and only persisted when the package is submitted, by
 * analyze_plan_submittal.
 */
const PlanReviewAdd = () => {
	// Tells Ollie what the reader is looking at.
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen:
				"The screen for starting a new plan review. The reader is choosing the property, the authority having jurisdiction and the kind of submittal before uploading anything."
		})
	);

	const navigate = useNavigate();
	const { loggedUserValue } = useLoggedUser();

	// The organization is not chosen on this screen: the review always runs for the user's own.
	const organizationId = loggedUserValue.organization_id ?? "";
	const [propertyId, setPropertyId] = useState<string>("");
	// An authority user only ever reviews for its own jurisdiction, so theirs is the
	// answer and there is nothing to look up.
	const lockedJurisdictionId = isAuthorityRole(loggedUserValue.role)
		? loggedUserValue.organization?.jurisdiction_id || undefined
		: undefined;
	// Fixed for the whole project: every submission is another attempt at the same
	// submittal, so it is chosen here and never asked for again.
	const [submissionTypeId, setSubmissionTypeId] = useState<string>("");

	// Fixed temp id so the staged property keeps its identity across re-renders.
	const [newPropertyTempId] = useState<string>(`${TEMP_PROPERTY_PREFIX}${nanoid()}`);
	const [isCreatePropertyModalOpen, setIsCreatePropertyModalOpen] = useState(false);
	// Controlled so the "Add new property" row can close the list before the modal opens.
	const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);
	const [isSavingProperty, setIsSavingProperty] = useState(false);
	// The raw form output is kept to re-open the modal for editing; the uploaded
	// input is what actually travels to the backend.
	const [newPropertyFormOutput, setNewPropertyFormOutput] = useState<PropertyFormOutput | null>(null);
	const [newPropertyInput, setNewPropertyInput] = useState<CreatePropertyInput | null>(null);

	// The AHJ still fills itself in for everybody. What only the platform admin sees is
	// the commentary about how it got there: for anybody else it is noise about internals
	// they cannot act on, since loading the maps is not part of their job.
	const canSeeJurisdictionInternals = isPlatformAdminRole(loggedUserValue.role);
	// The applicant needs to know that Ollie is not the AHJ. An authority user is the AHJ, so it keeps the requirements panel.
	const showsApplicantGuidance = !isAuthorityRole(loggedUserValue.role);

	const { data: propertiesData, loading: loadingProperties } = useGetPropertiesByOrganizationIdQuery({
		skip: !organizationId,
		variables: { organization_id: organizationId },
		fetchPolicy: "cache-and-network",
		notifyOnNetworkStatusChange: true,
		onError: useCallback((err: ApolloError) => console.error("getPropertiesByOrganizationId error:", err), [])
	});

	const propertyOptions = useMemo(
		() => [
			...(propertiesData?.properties ?? []).map(p => ({
				label: p.name,
				value: p.id,
				address: getPropertyAddress(p.address, p.standardized_address)
			})),
			...(newPropertyInput
				? [
						{
							label: `${newPropertyInput.name} (New)`,
							value: newPropertyTempId,
							address: newPropertyInput.address ?? ""
						}
					]
				: [])
		],
		[propertiesData, newPropertyInput, newPropertyTempId]
	);

	const isNewProperty = !!newPropertyInput && propertyId === newPropertyTempId;

	const { data: jurisdictionsData, loading: loadingJurisdictions } = useGetJurisdictionsQuery({
		fetchPolicy: "cache-first",
		onError: useCallback((err: ApolloError) => console.error("getJurisdictions error:", err), [])
	});

	const jurisdictionOptions = useMemo(
		() => (jurisdictionsData?.jurisdictions ?? []).map(j => ({ label: j.name, value: j.id })),
		[jurisdictionsData]
	);

	// The AHJ comes from where the property is and what is being submitted, so the
	// applicant does not have to know which office enforces the code at that address.
	// The dropdown below stays for the cases the lookup cannot settle.
	const {
		jurisdictionId,
		selectJurisdiction,
		applySuggestion,
		suggestion: jurisdictionSuggestion,
		loading: resolvingJurisdiction,
		isAutoSelected: isJurisdictionAutoSelected,
		isLocked: isJurisdictionLocked
	} = useResolvedJurisdiction({
		propertyId: isNewProperty ? undefined : propertyId || undefined,
		address: isNewProperty ? (newPropertyInput?.address ?? undefined) : undefined,
		standardizedAddress: isNewProperty ? (newPropertyInput?.standardized_address ?? undefined) : undefined,
		submissionTypeId: submissionTypeId || undefined,
		lockedJurisdictionId
		// No gate here on purpose: the submittal types are per-AHJ, so the jurisdiction
		// has to be resolved before there is a type to choose. The lookup runs again on
		// its own once one is picked, in case that type belongs to a different desk.
	});

	// The catalogue is per-AHJ (global baseline + whatever the jurisdiction added), so
	// there is nothing to offer until one is picked.
	const { data: submissionTypesData, loading: loadingSubmissionTypes } = useGetPlanReviewSubmissionTypesQuery({
		skip: !jurisdictionId,
		variables: { jurisdictionId },
		fetchPolicy: "cache-first",
		onError: useCallback((err: ApolloError) => console.error("getPlanReviewSubmissionTypes error:", err), [])
	});

	const submissionTypeOptions = useMemo(
		() => (submissionTypesData?.plan_review_submission_types ?? []).map(t => ({ label: t.name, value: t.id })),
		[submissionTypesData]
	);

	// The catalogue belongs to the AHJ, so a type picked under a different one is not
	// on offer any more and has to go.
	useEffect(() => {
		if (!submissionTypeId || loadingSubmissionTypes) return;
		if (submissionTypeOptions.some(option => option.value === submissionTypeId)) return;
		setSubmissionTypeId("");
	}, [submissionTypeId, submissionTypeOptions, loadingSubmissionTypes]);

	// What Ollie charges for this jurisdiction + submission type (MWA-788). A pair with
	// no fee schedule configured stays free, same as every plan review before this
	// feature — feeSchedule is then simply empty and nothing below renders.
	const [feeVariableValues, setFeeVariableValues] = useState<Record<string, string>>({});

	useEffect(() => {
		setFeeVariableValues({});
	}, [jurisdictionId, submissionTypeId]);

	const { data: feeScheduleData } = useGetAhjFeeScheduleQuery({
		skip: !jurisdictionId || !submissionTypeId,
		variables: { jurisdictionId, submissionTypeId },
		fetchPolicy: "cache-first",
		onError: useCallback((err: ApolloError) => console.error("getAhjFeeSchedule error:", err), [])
	});
	const feeSchedule = feeScheduleData?.ahj_fee_schedules?.[0];
	const feeVariables = feeSchedule?.ahj_fee_schedule_variables ?? [];

	const feeCalculationVariables = useMemo(
		() =>
			Object.entries(feeVariableValues)
				.filter(([, value]) => value.trim() !== "")
				.map(([variable_key, value]) => ({ variable_key, value: Number(value) })),
		[feeVariableValues]
	);

	const { data: calculatedFeeData, loading: loadingFee } = useCalculatePlanReviewFeeQuery({
		skip: !jurisdictionId || !submissionTypeId,
		variables: {
			input: {
				jurisdiction_id: jurisdictionId,
				submission_type_id: submissionTypeId,
				variables: feeCalculationVariables
			}
		},
		fetchPolicy: "network-only",
		onError: useCallback((err: ApolloError) => console.error("calculatePlanReviewFee error:", err), [])
	});

	// Every keystroke in a variable field changes this query's variables, and with
	// network-only that clears its data for the moment the new request is in flight —
	// which used to unmount the whole fee section (including the input being typed in)
	// on every character. Keeping the last result for the CURRENT jurisdiction + type
	// pair around bridges that gap; it is dropped the instant either one changes, so a
	// switch to a pair with no fee schedule never shows the previous pair's numbers.
	const feeResultPairKey = `${jurisdictionId}|${submissionTypeId}`;
	const lastFeeResultRef = useRef<{
		pairKey: string;
		result: CalculatePlanReviewFeeQuery["calculate_plan_review_fee"] | null;
	}>({ pairKey: feeResultPairKey, result: null });
	if (lastFeeResultRef.current.pairKey !== feeResultPairKey) {
		lastFeeResultRef.current = { pairKey: feeResultPairKey, result: null };
	}
	if (calculatedFeeData?.calculate_plan_review_fee) {
		lastFeeResultRef.current.result = calculatedFeeData.calculate_plan_review_fee;
	}
	const feeResult = calculatedFeeData?.calculate_plan_review_fee ?? lastFeeResultRef.current.result;
	// Nothing to collect and nothing to pay: the pair has no fee schedule.
	const hasFee = !!feeResult?.has_fee;
	const missingRequiredFeeVariables = feeResult?.missing_required ?? [];

	const setSelection = usePlanReviewFlowStore(state => state.setSelection);

	// Drops the staged property so a new one can be created in its place.
	const clearStagedProperty = useCallback(() => {
		setNewPropertyFormOutput(null);
		setNewPropertyInput(null);
		setPropertyId(current => (current === newPropertyTempId ? "" : current));
	}, [newPropertyTempId]);

	// Uploads the form's files to Storage right away (nothing is written to the
	// database) so what we keep is a plain, serializable CreatePropertyInput.
	const handleSaveNewProperty = useCallback(
		async (output: PropertyFormOutput) => {
			setIsSavingProperty(true);
			try {
				const input = await uploadPropertyFormData(output, loggedUserValue);
				// The photo is already uploaded, so the draft keeps its URL instead of the local file.
				// This shows the photo when the draft is edited and avoids uploading it again.
				setNewPropertyFormOutput({ ...output, propertyPhotoFile: null, existingPhotoUrl: input.photo_link ?? "" });
				setNewPropertyInput(input);
				setPropertyId(newPropertyTempId);
				setIsCreatePropertyModalOpen(false);
			} catch (error) {
				console.error("Plan review new property upload failed:", error);
				message.error("We couldn't save the property. Please try again.");
			} finally {
				setIsSavingProperty(false);
			}
		},
		[loggedUserValue, newPropertyTempId]
	);

	const canStart =
		!!organizationId &&
		!!propertyId &&
		!!jurisdictionId &&
		!!submissionTypeId &&
		(!hasFee || missingRequiredFeeVariables.length === 0);

	const handleBeginUpload = useCallback(() => {
		if (!canStart) return;

		// The job is created on submit (analyze_plan_submittal); here we only store the
		// chosen context — including anything staged for creation — and move to the
		// upload step. Persisted so it survives a refresh.
		setSelection({
			organizationId,
			// A staged property has no real id yet — the backend fills it in.
			propertyId: isNewProperty ? "" : propertyId,
			jurisdictionId,
			submissionTypeId,
			propertyName: propertyOptions.find(p => p.value === propertyId)?.label ?? "",
			newOrganization: null,
			newProperty: isNewProperty ? newPropertyInput : null,
			newUser: null,
			feeVariableInputs: hasFee ? feeCalculationVariables : undefined
		});
		navigate(routeNames.PLAN_REVIEW_COLLECTION);
	}, [
		canStart,
		organizationId,
		propertyId,
		jurisdictionId,
		submissionTypeId,
		propertyOptions,
		isNewProperty,
		newPropertyInput,
		loggedUserValue,
		hasFee,
		feeCalculationVariables,
		setSelection,
		navigate
	]);

	const isLoading = loadingProperties || loadingJurisdictions || loadingSubmissionTypes || isSavingProperty;

	const applicantGuidanceSection = (
		<div className="psf-guidance-section">
			<div className="psf-guidance">
				<div className="psf-guidance-header">
					<span className="psf-guidance-header-icon">
						<CircleCheck size={20} />
					</span>
					<div className="psf-guidance-header-text">
						<h3 className="psf-guidance-title">Fewer comment letters, faster permits</h3>
						<p className="psf-guidance-description">
							Ollie reviews your plans against the applicable codes and flags issues you can fix now, so your
							application is more likely to be approved without comment letters and resubmittal delays.
						</p>
					</div>
				</div>
				<ul className="psf-guidance-list">
					{APPLICANT_GUIDANCE_ITEMS.map(item => {
						const ItemIcon = item.icon;
						return (
							<li key={item.title} className="psf-guidance-item">
								<span className="psf-guidance-item-icon">
									<ItemIcon size={16} />
								</span>
								<p className="psf-guidance-item-text">
									<strong>{item.title}</strong> {item.text}
								</p>
							</li>
						);
					})}
				</ul>
			</div>

			<div className="psf-add-files-row">
				<button
					className={`psf-add-files-btn ${!canStart ? "psf-add-files-btn-disabled" : ""}`}
					onClick={handleBeginUpload}
					disabled={!canStart}
				>
					<span>Add Your Files</span>
					<ChevronRight size={16} className="psf-add-files-btn-icon" />
				</button>
			</div>
		</div>
	);

	// "Create new…" row appended to a dropdown, matching the inspection setup flow.
	const renderCreateRow = (label: string, onClick: () => void) => (menu: React.ReactNode) => (
		<>
			{menu}
			<Divider className="psf-select-divider" />
			<Button type="text" className="psf-select-create-btn" icon={<Plus size={16} />} onMouseDown={onClick}>
				{label}
			</Button>
		</>
	);

	return (
		<>
			<OllieLayout>
				<div className="psf-page">
					<div className="psf-shell">
						<div className="psf-card">
							<div className="psf-intro">
								<div className="psf-avatar">
									<img src={`${process.env.PUBLIC_URL}/assets/images/icons/ollie-owl.svg`} alt="Ollie AI" />
								</div>
								<div className="psf-intro-text">
									<h1 className="psf-title">Plan Review</h1>
									<p className="psf-subtitle">
										Upload your submittal package and Ollie will pre-screen before official AHJ plan review.
									</p>
								</div>
							</div>

							{canSeeJurisdictionInternals && (
								<TestingGuide
									title="What this screen does for you now"
									summary={`The AHJ is worked out from the property's address instead of being asked for, and that unlocks the field below it: the list of project types belongs to one AHJ, so it used to sit empty until you picked one by hand.`}
									steps={[
										<>Pick a property with a real address.</>,
										<>
											The <strong>AHJ</strong> fills in on its own, with a line underneath saying how it got there.
										</>,
										<>
											<strong>Project Type</strong> fills its list as a result, without you touching the AHJ first.
										</>,
										<>
											Pick a type. The AHJ can change with it: some counties publish one office for site and subdivision
											review and a different one for building permits, and we follow that.
										</>
									]}
									notMistakes={[
										<>
											<strong>An amber &quot;please check this is right&quot;.</strong> The answer came from matching
											the city name in the address, not from a map of the area. Load that region&apos;s map in{" "}
											<strong>Jurisdiction Maps</strong> and the same addresses stop asking.
										</>,
										<>
											<strong>The AHJ is looked up before you pick a project type.</strong> It has to be: the list of
											types belongs to an AHJ, so there would be nothing to choose from otherwise. Picking one runs the
											lookup again, in case that type belongs to a different office.
										</>
									]}
								/>
							)}

							<div className="psf-selectors">
								<div className="psf-field">
									<label className="psf-label">Property</label>
									<Select
										className="psf-select"
										showSearch
										optionFilterProp="label"
										placeholder="Select property..."
										disabled={!organizationId}
										value={propertyId || undefined}
										options={propertyOptions}
										onChange={setPropertyId}
										open={isPropertyDropdownOpen}
										onDropdownVisibleChange={setIsPropertyDropdownOpen}
										loading={loadingProperties}
										optionRender={opt => (
											<div className="psf-option">
												<span className="psf-option-name">{opt.data.label}</span>
												{opt.data.address ? <span className="psf-option-sub">{opt.data.address}</span> : null}
											</div>
										)}
										dropdownRender={renderCreateRow("Add new property", () => {
											setIsPropertyDropdownOpen(false);
											setIsCreatePropertyModalOpen(true);
											setPropertyId("");
										})}
									/>
								</div>
								<div className="psf-field">
									<label className="psf-label">AHJ (Jurisdiction)</label>
									<Select
										className="psf-select"
										showSearch
										optionFilterProp="label"
										placeholder={propertyId ? "Select jurisdiction..." : "Select a property first"}
										value={jurisdictionId || undefined}
										options={jurisdictionOptions}
										onChange={selectJurisdiction}
										loading={loadingJurisdictions || resolvingJurisdiction}
										disabled={isJurisdictionLocked}
									/>
									{!isJurisdictionLocked && canSeeJurisdictionInternals && (
										<JurisdictionAutoNote
											suggestion={jurisdictionSuggestion}
											loading={resolvingJurisdiction}
											isAutoSelected={isJurisdictionAutoSelected}
											onApplySuggestion={applySuggestion}
										/>
									)}
								</div>
								<div className="psf-field">
									<label className="psf-label">Project Type</label>
									<Select
										className="psf-select"
										placeholder={jurisdictionId ? "Select project type" : "Select a jurisdiction first"}
										disabled={!jurisdictionId}
										value={submissionTypeId || undefined}
										options={submissionTypeOptions}
										onChange={setSubmissionTypeId}
										loading={loadingSubmissionTypes}
										notFoundContent={
											<span className="psf-hint">This authority has no project types set up yet.</span>
										}
									/>
									{/* The list belongs to one authority, so an authority nobody has set up yet
									    leaves this empty. Without saying so, the form dead-ends: the AHJ filled
									    itself in correctly and then nothing can be chosen, with no clue why. This
									    one stays visible for every role — it is the reason a required field they
									    have to fill is empty — but only an admin is told where to go, because
									    only an admin can go there. */}
									{jurisdictionId && !loadingSubmissionTypes && submissionTypeOptions.length === 0 && (
										<span className="psf-hint psf-hint-warn">
											No project types have been set up for this authority yet, so there is nothing to choose.
											{canSeeJurisdictionInternals && (
												<>
													{" "}
													An admin adds them under <strong>Admin → Submission Types</strong> (the screen where project types
													are managed), and they only need to be set up once per authority.
												</>
											)}
										</span>
									)}
								</div>
							</div>

							{hasFee && (
								<div className="psf-fee-section">
									{feeVariables.length > 0 && (
										<div className="psf-fee-variables">
											{feeVariables.map(variable => (
												<div className="psf-field" key={variable.id}>
													<label className="psf-label">
														{variable.variable_label}
														{!variable.is_required && " (optional)"}
													</label>
													<Input
														className="psf-select psf-fee-variable-input"
														type="number"
														min={0}
														placeholder="0"
														value={feeVariableValues[variable.variable_key] ?? ""}
														onChange={e =>
															setFeeVariableValues(prev => ({
																...prev,
																[variable.variable_key]: e.target.value
															}))
														}
													/>
												</div>
											))}
										</div>
									)}

									<div className="psf-fee-summary">
										{loadingFee ? (
											<span className="psf-hint">Calculating your Ollie fee…</span>
										) : missingRequiredFeeVariables.length > 0 ? (
											<span className="psf-hint">
												Fill in every required field above to see your Ollie pre-screening fee.
											</span>
										) : (
											<span className="psf-fee-amount">
												Ollie pre-screening fee: <strong>${feeResult?.amount?.toFixed(2)}</strong>
											</span>
										)}
										<p className="psf-hint psf-fee-note">
											We&apos;ll settle up after you have a chance to see a summary of Ollie&apos;s review, and your
											price includes {feeSchedule?.included_cycles_limit} resubmittals for this project.
										</p>
									</div>
								</div>
							)}

							{/* What will be created along with the review, and how to edit it */}
							{isNewProperty && (
								<div className="psf-staged">
									{isNewProperty && (
										<div className="psf-staged-row">
											<div className="psf-staged-info">
												<span className="psf-staged-label">New property</span>
												<span className="psf-staged-value">
													{newPropertyInput?.name}
													<span className="psf-staged-badge">New</span>
												</span>
												{newPropertyInput?.address ? (
													<span className="psf-staged-sub">{newPropertyInput.address}</span>
												) : null}
											</div>
											<div className="psf-staged-actions">
												<button
													type="button"
													className="psf-staged-btn"
													title="Edit"
													onClick={() => setIsCreatePropertyModalOpen(true)}
												>
													<Pencil size={16} />
												</button>
												<button type="button" className="psf-staged-btn" title="Remove" onClick={clearStagedProperty}>
													<Trash2 size={16} />
												</button>
											</div>
										</div>
									)}

									<p className="psf-staged-note">Nothing is created until you submit the package for review.</p>
								</div>
							)}

							{showsApplicantGuidance ? (
								applicantGuidanceSection
							) : (
								<div className="psf-bottom">
									<div className="psf-requirements">
										<h3 className="psf-requirements-title">Requirements</h3>
										<ul className="psf-requirements-list">
											{REQUIREMENTS.map(req => (
												<li key={req} className="psf-requirements-item">
													<Check className="psf-requirements-check" size={16} />
													<span>{req}</span>
												</li>
											))}
										</ul>
									</div>

									<button
										className={`psf-begin-btn ${!canStart ? "psf-begin-btn-disabled" : ""}`}
										onClick={handleBeginUpload}
										disabled={!canStart}
									>
										<span>Add Your Files</span>
										<ChevronRight size={16} />
									</button>
								</div>
							)}
						</div>
					</div>
				</div>
			</OllieLayout>

			{isCreatePropertyModalOpen && (
				<AddPropertyModal
					initialValues={newPropertyFormOutput}
					organizationId={organizationId}
					onCancel={() => setIsCreatePropertyModalOpen(false)}
					onSubmit={handleSaveNewProperty}
				/>
			)}

			<LoadingOverlay isLoading={isLoading} tip={isSavingProperty ? "Saving property…" : ""} />
		</>
	);
};

export default PlanReviewAdd;
